import { X, Calendar, Mail, Edit2, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useEvent, useDeleteEvent, useCompleteEvent, useCancelEvent } from '@/hooks/useEvents';
import { formatDate } from '@/utils/formatters';

const EVENT_TYPE_LABELS = {
  deadline: 'Deadline',
  appointment: 'Appointment',
  sale_end: 'Sale End',
  reminder: 'Reminder'
};

const EVENT_TYPE_COLORS = {
  deadline: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  appointment: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  sale_end: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200',
  reminder: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200'
};

export default function EventDetailDialog({ eventId, onClose, onEmailClick }) {
  const { data: event, isLoading } = useEvent(eventId);
  const deleteEvent = useDeleteEvent();
  const completeEvent = useCompleteEvent();
  const cancelEvent = useCancelEvent();

  const handleDelete = async () => {
    if (!confirm('Delete this event?')) return;

    try {
      await deleteEvent.mutateAsync(eventId);
      onClose();
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  const handleComplete = async () => {
    try {
      await completeEvent.mutateAsync(eventId);
    } catch (err) {
      console.error('Failed to complete event:', err);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel this event?')) return;

    try {
      await cancelEvent.mutateAsync(eventId);
    } catch (err) {
      console.error('Failed to cancel event:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full p-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  const eventDate = new Date(event.event_date);
  const isUpcoming = eventDate > new Date() && event.status === 'pending';
  const isPast = eventDate < new Date() && event.status === 'pending';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Event Details</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold mb-3">{event.title}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              {event.event_type && (
                <Badge className={EVENT_TYPE_COLORS[event.event_type] || ''}>
                  {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                </Badge>
              )}
              {event.status === 'pending' && (
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                  Pending
                </Badge>
              )}
              {event.status === 'completed' && (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200">
                  Completed
                </Badge>
              )}
              {event.status === 'cancelled' && (
                <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-950 dark:text-gray-200">
                  Cancelled
                </Badge>
              )}
              {isUpcoming && (
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                  Upcoming
                </Badge>
              )}
              {isPast && (
                <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200">
                  Overdue
                </Badge>
              )}
            </div>
          </div>

          {/* Date */}
          <div className="p-4 rounded-lg border bg-muted/50">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Calendar className="h-4 w-4" />
              <span className="text-sm font-medium">Event Date</span>
            </div>
            <p className="text-lg font-medium">{formatDate(event.event_date)}</p>
          </div>

          {/* Description */}
          {event.description && (
            <div>
              <h3 className="font-medium mb-2">Description</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {event.description}
              </p>
            </div>
          )}

          {/* Linked Emails */}
          {event.emails && event.emails.length > 0 && (
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Linked Emails ({event.emails.length})
              </h3>
              <div className="space-y-2">
                {event.emails.map(email => (
                  <div
                    key={email.id}
                    onClick={() => onEmailClick?.(email.id)}
                    className="p-3 rounded-lg border bg-background hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{email.subject}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          From: {email.from_address}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(email.date)}
                        </p>
                      </div>
                      {email.relevance && (
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {email.relevance}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 p-4 border-t bg-muted/50">
          <div className="flex items-center gap-2">
            {event.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleComplete}
                  disabled={completeEvent.isPending}
                  className="flex items-center gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Mark Complete
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelEvent.isPending}
                  className="flex items-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Cancel Event
                </Button>
              </>
            )}
          </div>

          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={deleteEvent.isPending}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
